import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import {
  PencilIcon,
  TrashIcon,
  StarIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  ArrowUturnLeftIcon,
  FolderArrowDownIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useUIStore } from '../../stores/uiStore';
import { useFileStore } from '../../stores/fileStore';
import { filesApi } from '../../api/files';
import { foldersApi } from '../../api/folders';
import { useToast } from '../ui/Toaster';
import { useLocation } from 'react-router-dom';
import { zipSync } from 'fflate';
import clsx from 'clsx';

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
}

interface MenuItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  danger?: boolean;
  divider?: boolean;
  disabled?: boolean;
  activeIcon?: React.ComponentType<{ className?: string }>;
  isActive?: boolean;
}

export function ContextMenu({ x, y, onClose }: ContextMenuProps) {
  const { t } = useTranslation('files');
  const menuRef = useRef<HTMLDivElement>(null);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const toast = useToast();
  const location = useLocation();

  const contextMenuTarget = useUIStore((s) => s.contextMenuTarget);
  const openRenameModal = useUIStore((s) => s.openRenameModal);
  const openCreateLinkModal = useUIStore((s) => s.openCreateLinkModal);
  const openDeleteConfirm = useUIStore((s) => s.openDeleteConfirm);
  const openMoveModal = useUIStore((s) => s.openMoveModal);

  const files = useFileStore((s) => s.files);
  const updateFile = useFileStore((s) => s.updateFile);
  const removeFile = useFileStore((s) => s.removeFile);

  const isTrashPage = location.pathname === '/trash';

  const currentFile =
    contextMenuTarget?.type === 'file'
      ? files.find((f) => f.id === contextMenuTarget.id)
      : null;

  // Adjust position if menu would overflow
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (rect.right > viewportWidth) {
        menuRef.current.style.left = `${x - rect.width}px`;
      }
      if (rect.bottom > viewportHeight) {
        menuRef.current.style.top = `${y - rect.height}px`;
      }
    }
  }, [x, y]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleDownload = async () => {
    if (!contextMenuTarget || contextMenuTarget.type !== 'file') return;
    try {
      const { downloadUrl } = await filesApi.getDownloadUrl(contextMenuTarget.id);
      window.open(downloadUrl, '_blank');
      onClose();
    } catch {
      toast.error(t('toast.downloadFailed'));
    }
  };

  const handleStar = async () => {
    if (!contextMenuTarget || contextMenuTarget.type !== 'file' || !currentFile) return;
    try {
      await filesApi.update(contextMenuTarget.id, { isStarred: !currentFile.isStarred });
      updateFile(contextMenuTarget.id, { isStarred: !currentFile.isStarred });
      toast.success(currentFile.isStarred ? t('toast.removedFromStarred') : t('toast.addedToStarred'));
      onClose();
    } catch {
      toast.error(t('toast.updateFailed'));
    }
  };

  const handleRestore = async () => {
    if (!contextMenuTarget || contextMenuTarget.type !== 'file') return;
    try {
      await filesApi.restore(contextMenuTarget.id);
      removeFile(contextMenuTarget.id);
      toast.success(t('toast.fileRestored'));
      onClose();
    } catch {
      toast.error(t('toast.restoreFailed'));
    }
  };

  const handleRename = () => {
    openRenameModal();
    onClose();
  };

  const handleCreateLink = () => {
    openCreateLinkModal();
    onClose();
  };

  const handleDelete = () => {
    openDeleteConfirm();
    onClose();
  };

  const handleMove = () => {
    if (!contextMenuTarget || contextMenuTarget.type !== 'file') return;
    openMoveModal([contextMenuTarget.id]);
    onClose();
  };

  const handleDownloadFolderAsZip = async () => {
    if (!contextMenuTarget || contextMenuTarget.type !== 'folder') return;

    setIsDownloadingZip(true);
    const toastId = toast.loading(t('toast.preparingZip'));

    try {
      // Get list of files in folder
      const { folderName, files: folderFiles } = await foldersApi.getFilesForDownload(
        contextMenuTarget.id
      );

      if (folderFiles.length === 0) {
        toast.dismiss(toastId);
        toast.error(t('toast.folderEmpty'));
        onClose();
        return;
      }

      toast.dismiss(toastId);
      const downloadToastId = toast.loading(t('toast.downloadingFiles', { count: folderFiles.length }));

      // Download all files and collect their data
      const zipData: Record<string, Uint8Array> = {};
      let downloadedCount = 0;

      for (const file of folderFiles) {
        try {
          const { downloadUrl } = await filesApi.getDownloadUrl(file.id);
          const response = await fetch(downloadUrl);
          const arrayBuffer = await response.arrayBuffer();
          zipData[file.relativePath] = new Uint8Array(arrayBuffer);
          downloadedCount++;

          // Update progress
          toast.dismiss(downloadToastId);
          toast.loading(t('toast.downloadedProgress', { current: downloadedCount, total: folderFiles.length }));
        } catch (err) {
          console.error(`Failed to download ${file.name}:`, err);
        }
      }

      toast.dismiss(downloadToastId);

      if (Object.keys(zipData).length === 0) {
        toast.error(t('toast.downloadFailed'));
        onClose();
        return;
      }

      // Create ZIP file
      const zipToastId = toast.loading(t('toast.creatingZip'));
      const zipped = zipSync(zipData, { level: 6 });
      toast.dismiss(zipToastId);

      // Trigger download
      const blob = new Blob([new Uint8Array(zipped)], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folderName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(
        t('toast.zipDownloaded'),
        t('toast.zipDownloadedDesc', { count: Object.keys(zipData).length, name: folderName })
      );
      onClose();
    } catch (err: unknown) {
      toast.dismiss();
      const message = err instanceof Error ? err.message : t('toast.downloadFailed');
      toast.error(message);
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const trashMenuItems: MenuItem[] = [
    {
      icon: ArrowUturnLeftIcon,
      label: t('common:buttons.restore'),
      onClick: handleRestore,
    },
    {
      icon: TrashIcon,
      label: t('contextMenu.deleteForever'),
      onClick: handleDelete,
      danger: true,
      divider: true,
    },
  ];

  const normalMenuItems: MenuItem[] = [
    ...(contextMenuTarget?.type === 'file'
      ? [
          {
            icon: ArrowDownTrayIcon,
            label: t('common:buttons.download'),
            onClick: handleDownload,
          },
        ]
      : [
          {
            icon: FolderArrowDownIcon,
            label: isDownloadingZip ? t('contextMenu.downloading') : t('contextMenu.downloadAsZip'),
            onClick: handleDownloadFolderAsZip,
            disabled: isDownloadingZip,
          },
        ]),
    {
      icon: LinkIcon,
      label: t('contextMenu.shareLink'),
      onClick: handleCreateLink,
      divider: true,
    },
    {
      icon: PencilIcon,
      label: t('common:buttons.rename'),
      onClick: handleRename,
    },
    ...(contextMenuTarget?.type === 'file'
      ? [
          {
            icon: StarIcon,
            activeIcon: StarSolidIcon,
            label: currentFile?.isStarred ? t('contextMenu.removeStar') : t('contextMenu.star'),
            onClick: handleStar,
            isActive: currentFile?.isStarred,
          },
          {
            icon: FolderArrowDownIcon,
            label: t('contextMenu.moveTo'),
            onClick: handleMove,
            divider: true,
          },
        ]
      : []),
    {
      icon: TrashIcon,
      label: t('contextMenu.moveToTrash'),
      onClick: handleDelete,
      danger: true,
      divider: true,
    },
  ];

  const menuItems = isTrashPage ? trashMenuItems : normalMenuItems;

  return (
    <motion.div
      ref={menuRef}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.1 }}
      className="fixed z-50 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 py-2 min-w-[180px]"
      style={{ left: x, top: y }}
    >
      {menuItems.map((item, index) => (
        <div key={index}>
          {item.divider && index > 0 && (
            <div className="h-px bg-gray-200 dark:bg-slate-700 my-1" />
          )}
          <button
            onClick={item.onClick}
            disabled={item.disabled}
            className={clsx(
              'w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors',
              item.danger
                ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700',
              item.disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {item.isActive && item.activeIcon ? (
              <item.activeIcon className="w-4 h-4 text-yellow-400" />
            ) : (
              <item.icon className="w-4 h-4" />
            )}
            {item.label}
          </button>
        </div>
      ))}
    </motion.div>
  );
}

// Wrapper component to handle context menu state
export function ContextMenuWrapper() {
  const contextMenuTarget = useUIStore((s) => s.contextMenuTarget);
  const contextMenuPosition = useUIStore((s) => s.contextMenuPosition);
  const closeContextMenu = useUIStore((s) => s.closeContextMenu);

  if (!contextMenuTarget || !contextMenuPosition) return null;

  return (
    <AnimatePresence>
      <ContextMenu
        x={contextMenuPosition.x}
        y={contextMenuPosition.y}
        onClose={closeContextMenu}
      />
    </AnimatePresence>
  );
}
